
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Building2, TrendingUp, PieChart, Calendar, Plus, Filter, Download } from "lucide-react";
import NavBar from "@/components/NavBar";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface CapitalProject {
  id: string;
  name: string;
  category: string;
  budget: number;
  spent: number;
  status: 'planning' | 'approved' | 'active' | 'completed' | 'on-hold';
  startDate: string;
  endDate: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  roi: number;
}

export default function CapitalProgramme() {
  const [projects, setProjects] = useState<CapitalProject[]>([
    {
      id: '1',
      name: 'New Office Building',
      category: 'Infrastructure',
      budget: 5000000,
      spent: 1500000,
      status: 'active',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      priority: 'high',
      roi: 12.5
    },
    {
      id: '2',
      name: 'IT System Upgrade',
      category: 'Technology',
      budget: 800000,
      spent: 200000,
      status: 'approved',
      startDate: '2024-03-01',
      endDate: '2024-08-31',
      priority: 'medium',
      roi: 18.2
    },
    {
      id: '3',
      name: 'Manufacturing Equipment',
      category: 'Equipment',
      budget: 2500000,
      spent: 2500000,
      status: 'completed',
      startDate: '2023-06-01',
      endDate: '2023-12-31',
      priority: 'critical',
      roi: 15.8
    }
  ]);

  const [newProject, setNewProject] = useState({
    name: '',
    category: '',
    budget: '',
    startDate: '',
    endDate: '',
    priority: 'medium' as CapitalProject['priority']
  });

  const [filter, setFilter] = useState({
    status: 'all',
    category: 'all',
    priority: 'all'
  });

  const totalBudget = projects.reduce((sum, p) => sum + p.budget, 0);
  const totalSpent = projects.reduce((sum, p) => sum + p.spent, 0);
  const averageROI = projects.reduce((sum, p) => sum + p.roi, 0) / projects.length;

  const getStatusColor = (status: CapitalProject['status']) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-yellow-100 text-yellow-800';
      case 'planning': return 'bg-gray-100 text-gray-800';
      case 'on-hold': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: CapitalProject['priority']) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 text-red-800';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredProjects = projects.filter(project => {
    if (filter.status !== 'all' && project.status !== filter.status) return false;
    if (filter.category !== 'all' && project.category !== filter.category) return false;
    if (filter.priority !== 'all' && project.priority !== filter.priority) return false;
    return true;
  });

  const addProject = () => {
    if (newProject.name && newProject.category && newProject.budget) {
      const project: CapitalProject = {
        id: Date.now().toString(),
        name: newProject.name,
        category: newProject.category,
        budget: parseFloat(newProject.budget),
        spent: 0,
        status: 'planning',
        startDate: newProject.startDate,
        endDate: newProject.endDate,
        priority: newProject.priority,
        roi: 0
      };
      setProjects([...projects, project]);
      setNewProject({ name: '', category: '', budget: '', startDate: '', endDate: '', priority: 'medium' });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <NavBar />
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-3">
            <Building2 className="h-8 w-8 text-primary" />
            Capital Programme Management
          </h1>
          <p className="text-muted-foreground">
            Comprehensive capital investment planning, tracking, and portfolio management
          </p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="grid w-full grid-cols-5">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="projects">Projects</TabsTrigger>
            <TabsTrigger value="budget">Budget Planning</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
            <TabsTrigger value="reports">Reports</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">£{(totalBudget / 1000000).toFixed(1)}M</div>
                    <p className="text-sm text-muted-foreground">Total Budget</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">£{(totalSpent / 1000000).toFixed(1)}M</div>
                    <p className="text-sm text-muted-foreground">Total Spent</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">{averageROI.toFixed(1)}%</div>
                    <p className="text-sm text-muted-foreground">Average ROI</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-orange-600">{projects.length}</div>
                    <p className="text-sm text-muted-foreground">Active Projects</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <PieChart className="h-5 w-5" />
                    Budget Allocation by Category
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['Infrastructure', 'Technology', 'Equipment'].map((category) => {
                      const categoryProjects = projects.filter(p => p.category === category);
                      const categoryBudget = categoryProjects.reduce((sum, p) => sum + p.budget, 0);
                      const percentage = (categoryBudget / totalBudget) * 100;
                      
                      return (
                        <div key={category} className="space-y-2">
                          <div className="flex justify-between">
                            <span className="font-medium">{category}</span>
                            <span>£{(categoryBudget / 1000000).toFixed(1)}M ({percentage.toFixed(1)}%)</span>
                          </div>
                          <Progress value={percentage} className="w-full" />
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Project Status Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {['active', 'approved', 'completed', 'planning', 'on-hold'].map((status) => {
                      const statusProjects = projects.filter(p => p.status === status);
                      const count = statusProjects.length;
                      const percentage = (count / projects.length) * 100;
                      
                      return (
                        <div key={status} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge className={getStatusColor(status as CapitalProject['status'])}>
                              {status}
                            </Badge>
                            <span>{count} projects</span>
                          </div>
                          <span>{percentage.toFixed(1)}%</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="projects" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Capital Projects Portfolio</span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      <Filter className="h-4 w-4 mr-2" />
                      Filter
                    </Button>
                    <Button onClick={addProject}>
                      <Plus className="h-4 w-4 mr-2" />
                      New Project
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Select value={filter.status} onValueChange={(value) => setFilter({...filter, status: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="planning">Planning</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="on-hold">On Hold</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={filter.category} onValueChange={(value) => setFilter({...filter, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                      <SelectItem value="Technology">Technology</SelectItem>
                      <SelectItem value="Equipment">Equipment</SelectItem>
                    </SelectContent>
                  </Select>
                  
                  <Select value={filter.priority} onValueChange={(value) => setFilter({...filter, priority: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filter by priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priorities</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-4">
                  {filteredProjects.map((project) => (
                    <Card key={project.id}>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{project.name}</h3>
                            <p className="text-sm text-muted-foreground">{project.category}</p>
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center gap-4 text-sm">
                                <span>Budget: £{project.budget.toLocaleString()}</span>
                                <span>Spent: £{project.spent.toLocaleString()}</span>
                                <span>ROI: {project.roi}%</span>
                              </div>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <Calendar className="h-4 w-4" />
                                <span>{new Date(project.startDate).toLocaleDateString()} - {new Date(project.endDate).toLocaleDateString()}</span>
                              </div>
                            </div>
                            <div className="mt-2">
                              <div className="flex justify-between text-sm mb-1">
                                <span>Budget Utilization</span>
                                <span>{((project.spent / project.budget) * 100).toFixed(1)}%</span>
                              </div>
                              <Progress value={(project.spent / project.budget) * 100} className="w-full" />
                            </div>
                          </div>
                          <div className="flex flex-col gap-2 ml-4">
                            <Badge className={getStatusColor(project.status)}>
                              {project.status}
                            </Badge>
                            <Badge className={getPriorityColor(project.priority)}>
                              {project.priority}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="budget" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Add New Capital Project</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="projectName">Project Name</Label>
                    <Input
                      id="projectName"
                      placeholder="Enter project name"
                      value={newProject.name}
                      onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="category">Category</Label>
                    <Select value={newProject.category} onValueChange={(value) => setNewProject({...newProject, category: value})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Infrastructure">Infrastructure</SelectItem>
                        <SelectItem value="Technology">Technology</SelectItem>
                        <SelectItem value="Equipment">Equipment</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="budget">Budget (£)</Label>
                    <Input
                      id="budget"
                      type="number"
                      placeholder="1000000"
                      value={newProject.budget}
                      onChange={(e) => setNewProject({...newProject, budget: e.target.value})}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Start Date</Label>
                      <Input
                        id="startDate"
                        type="date"
                        value={newProject.startDate}
                        onChange={(e) => setNewProject({...newProject, startDate: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endDate">End Date</Label>
                      <Input
                        id="endDate"
                        type="date"
                        value={newProject.endDate}
                        onChange={(e) => setNewProject({...newProject, endDate: e.target.value})}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="priority">Priority</Label>
                    <Select value={newProject.priority} onValueChange={(value: CapitalProject['priority']) => setNewProject({...newProject, priority: value})}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                        <SelectItem value="critical">Critical</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Budget Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Approved Budget:</span>
                      <span className="text-lg font-bold">£{(totalBudget / 1000000).toFixed(2)}M</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Total Committed:</span>
                      <span className="text-lg font-bold text-orange-600">£{(totalSpent / 1000000).toFixed(2)}M</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="font-medium">Remaining Budget:</span>
                      <span className="text-lg font-bold text-green-600">£{((totalBudget - totalSpent) / 1000000).toFixed(2)}M</span>
                    </div>
                    <Progress value={(totalSpent / totalBudget) * 100} className="w-full" />
                    <div className="pt-4">
                      <h4 className="font-semibold mb-2">Budget by Year</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span>2024:</span>
                          <span>£{((totalBudget * 0.6) / 1000000).toFixed(1)}M</span>
                        </div>
                        <div className="flex justify-between">
                          <span>2025:</span>
                          <span>£{((totalBudget * 0.3) / 1000000).toFixed(1)}M</span>
                        </div>
                        <div className="flex justify-between">
                          <span>2026:</span>
                          <span>£{((totalBudget * 0.1) / 1000000).toFixed(1)}M</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="analytics" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Performance Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>On-Time Delivery:</span>
                      <span className="font-semibold text-green-600">85%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Budget Adherence:</span>
                      <span className="font-semibold text-blue-600">92%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Quality Score:</span>
                      <span className="font-semibold text-purple-600">4.2/5</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Stakeholder Satisfaction:</span>
                      <span className="font-semibold text-orange-600">88%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Risk Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>High Risk Projects:</span>
                      <span className="font-semibold text-red-600">2</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Medium Risk Projects:</span>
                      <span className="font-semibold text-yellow-600">3</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Low Risk Projects:</span>
                      <span className="font-semibold text-green-600">1</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Risk Mitigation Rate:</span>
                      <span className="font-semibold text-blue-600">78%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Financial Metrics</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Average ROI:</span>
                      <span className="font-semibold text-green-600">{averageROI.toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Payback Period:</span>
                      <span className="font-semibold text-blue-600">3.2 years</span>
                    </div>
                    <div className="flex justify-between">
                      <span>NPV:</span>
                      <span className="font-semibold text-purple-600">£2.1M</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IRR:</span>
                      <span className="font-semibold text-orange-600">16.8%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          <TabsContent value="reports" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Capital Programme Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Portfolio Summary</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Budget Analysis</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Performance Dashboard</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Executive Summary</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Risk Assessment</div>
                    </div>
                  </Button>
                  <Button variant="outline" className="h-20">
                    <div className="text-center">
                      <Download className="h-6 w-6 mx-auto mb-2" />
                      <div>Financial Analysis</div>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
